import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowDownAZ,
  ArrowUpAZ,
  Monitor,
  Rows3,
  CheckSquare,
  Square,
  ListFilter,
  ChevronDown,
} from "lucide-react";
import {
  MMK_FILTER_CATEGORIES,
  type MmkFilterCategory,
  type MmkFocusSessionCategory,
  type MmkItem,
  type MmkSortOrder,
  type MmkViewMode,
} from "../../types/mmk";
import { MmkHeader } from "./mmk-header";
import { MmkCardsGrid } from "./mmk-cards-grid";
import { MmkBatchActions } from "./mmk-batch-actions";
import { FocusSessionVariantModal } from "./focus-session-variant-modal";
import { getFocusSessionItems } from "../../hooks";
import { trackMmkEvent } from "../../utils/mmkAnalytics";
import { toast } from "react-toastify";
import { useMmkStore } from "../../stores/mmkStore";
import {
  MAX_PERSONAL_MMK_CARDS,
  type PersonalMmkFormPayload,
} from "../../helpers/mmkPersonal";
import {
  MmkPersonalCardDialog,
  type MmkPersonalCardDialogMode,
} from "./mmk-personal-card-dialog/mmk-personal-card-dialog";
import { MmkPageShell } from "./mmk-page-shell";
import {
  mmkFiltersWrapperStyles,
  mmkFilterSectionStyles,
  mmkFilterSectionLabelStyles,
  mmkCategoryTabsStyles,
  mmkControlsSectionStyles,
  mmkControlsRowStyles,
  getMmkTabButtonStyles,
} from "./styles";

interface MmkMainContentProps {
  items: MmkItem[];
  totalCount: number;
  masteredCount: number;
  totalToReviewCount: number;
  todayReviewCount: number;
  pinnedItems: MmkItem[];
  toReviewItems: MmkItem[];
  knownItems: MmkItem[];
  allKnown: boolean;
  activeCategory: MmkFilterCategory;
  categoryCounts: Record<MmkFilterCategory, number>;
  searchTerm: string;
  viewMode: MmkViewMode;
  sortOrder: MmkSortOrder;
  onSortOrderChange: (order: MmkSortOrder) => void;
  isSelectionMode: boolean;
  selectedIds: Set<string>;
  activeCardId: string | null;
  flippedCardId: string | null;
  variantModalOpen: boolean;
  focusSessionVariant: "quick_5" | "by_category" | "mixed";
  focusSessionCategory: MmkFocusSessionCategory | "";
  onCategoryChange: (cat: MmkFilterCategory) => void;
  onSearchChange: (value: string) => void;
  onViewModeChange: (mode: MmkViewMode) => void;
  onToggleSelectionMode: () => void;
  onClearSelection: () => void;
  onBatchHide: () => Promise<void>;
  onBatchPractice: () => void;
  onCardFlip: (id: string) => void;
  onCloseFlip: () => void;
  onPin: (id: string) => Promise<void>;
  onReviewLater: (id: string) => Promise<void>;
  onKnowIt: (id: string) => Promise<void>;
  onToggleSelect: (id: string) => void;
  onVariantModalOpen: () => void;
  onVariantModalClose: () => void;
  onVariantSelect: (v: "quick_5" | "by_category" | "mixed") => void;
  onCategorySelect: (c: MmkFocusSessionCategory | "") => void;
  onStartFocusSession: () => void;
  onStartQuiz: () => void;
  onAllPassedClick: () => void;
  // After edit, personal card id changes — keep flip/selection in sync.
  onPersonalCardIdReplaced?: (oldId: string, newId: string) => void;
  onPersonalCardDeleted?: (id: string) => void;
}

export const MmkMainContent: React.FC<MmkMainContentProps> = ({
  items,
  totalCount,
  masteredCount,
  totalToReviewCount,
  todayReviewCount,
  pinnedItems,
  toReviewItems,
  knownItems,
  allKnown,
  activeCategory,
  categoryCounts,
  searchTerm,
  viewMode,
  sortOrder,
  onSortOrderChange,
  isSelectionMode,
  selectedIds,
  activeCardId,
  flippedCardId,
  variantModalOpen,
  focusSessionVariant,
  focusSessionCategory,
  onCategoryChange,
  onSearchChange,
  onViewModeChange,
  onToggleSelectionMode,
  onClearSelection,
  onBatchHide,
  onBatchPractice,
  onCardFlip,
  onCloseFlip,
  onPin,
  onReviewLater,
  onKnowIt,
  onToggleSelect,
  onVariantModalOpen,
  onVariantModalClose,
  onVariantSelect,
  onCategorySelect,
  onStartFocusSession,
  onStartQuiz,
  onAllPassedClick,
  onPersonalCardIdReplaced,
  onPersonalCardDeleted,
}) => {
  const addPersonalMmkItem = useMmkStore((s) => s.addPersonalMmkItem);
  const editPersonalMmkItem = useMmkStore((s) => s.editPersonalMmkItem);
  const deletePersonalMmkItem = useMmkStore((s) => s.deletePersonalMmkItem);

  const [personalDialogOpen, setPersonalDialogOpen] = useState(false);
  const [personalDialogMode, setPersonalDialogMode] =
    useState<MmkPersonalCardDialogMode>("add");
  const [editingPersonalId, setEditingPersonalId] = useState<string | null>(
    null,
  );
  const [deleteDialogId, setDeleteDialogId] = useState<string | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [categoryMenuAnchor, setCategoryMenuAnchor] =
    useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isMobileCategory = useMediaQuery(theme.breakpoints.down("md"));
  const categoryMenuOpen = Boolean(categoryMenuAnchor);

  const sortedCategories = useMemo(
    () =>
      [...MMK_FILTER_CATEGORIES].sort(
        (a, b) => (categoryCounts[b] ?? 0) - (categoryCounts[a] ?? 0),
      ),
    [categoryCounts],
  );

  // Shared width so the trigger matches the open menu.
  const categorySelectWidth = 240;

  const personalCount = items.filter((i) => i.source === "personal").length;
  const editingPersonalItem =
    editingPersonalId && personalDialogMode === "edit"
      ? (items.find((i) => i.id === editingPersonalId) ?? null)
      : null;

  const openAddPersonalDialog = useCallback(() => {
    setPersonalDialogMode("add");
    setEditingPersonalId(null);
    setPersonalDialogOpen(true);
  }, []);

  const openEditPersonalDialog = useCallback((id: string) => {
    setPersonalDialogMode("edit");
    setEditingPersonalId(id);
    setPersonalDialogOpen(true);
  }, []);

  const handlePersonalDialogSave = useCallback(
    async (payload: PersonalMmkFormPayload) => {
      if (personalDialogMode === "add") {
        const res = await addPersonalMmkItem(payload);
        if (!res.ok) {
          if (res.error === "limit") {
            toast.error(
              `You can have at most ${MAX_PERSONAL_MMK_CARDS} personal cards`,
            );
          } else {
            toast.error("Could not save personal card. Try again");
          }
          return false;
        }
        toast.success("Personal card added");
        return;
      }
      if (!editingPersonalId) return;
      const res = await editPersonalMmkItem(editingPersonalId, payload);
      if (!res.ok) {
        toast.error("Could not update personal card. Try again");
        return false;
      }
      onPersonalCardIdReplaced?.(editingPersonalId, res.newId);
    },
    [
      personalDialogMode,
      addPersonalMmkItem,
      editPersonalMmkItem,
      editingPersonalId,
      onPersonalCardIdReplaced,
    ],
  );

  const openDeletePersonalDialog = useCallback((id: string) => {
    setDeleteDialogId(id);
  }, []);

  const closeDeletePersonalDialog = useCallback(() => {
    if (deleteInProgress) return;
    setDeleteDialogId(null);
  }, [deleteInProgress]);

  const confirmDeletePersonal = useCallback(async () => {
    if (!deleteDialogId) return;
    setDeleteInProgress(true);
    try {
      const id = deleteDialogId;
      const res = await deletePersonalMmkItem(id);
      if (!res.ok) {
        toast.error("Could not delete personal card. Try again");
        return;
      }
      onPersonalCardDeleted?.(id);
      trackMmkEvent("Delete personal MMM", { id });
      toast.success("Personal card removed");
      setDeleteDialogId(null);
    } finally {
      setDeleteInProgress(false);
    }
  }, [deleteDialogId, deletePersonalMmkItem, onPersonalCardDeleted]);

  const handleFilterClick = (cat: MmkFilterCategory) => {
    onCategoryChange(cat);
    onCloseFlip();
    setCategoryMenuAnchor(null);
    trackMmkEvent("Toggle filter", { filter: cat });
  };

  const handleViewClick = (mode: MmkViewMode) => {
    onViewModeChange(mode);
    onCloseFlip();
    trackMmkEvent("Toggle view", { view: mode });
  };

  const canStartFocusSession =
    focusSessionVariant === "by_category"
      ? getFocusSessionItems(
          items,
          focusSessionVariant,
          focusSessionCategory || undefined,
        ).length > 0
      : getFocusSessionItems(items, focusSessionVariant).length > 0;

  // Dismiss flip on outside click without a full-screen overlay (overlay blocked page scroll).
  useEffect(() => {
    if (!activeCardId) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(`[data-mmk-card-id="${activeCardId}"]`)) return;
      if (target.closest('[role="dialog"]')) return;
      onCloseFlip();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [activeCardId, onCloseFlip]);

  return (
    <MmkPageShell>
      <MmkHeader
        masteredCount={masteredCount}
        totalCount={totalCount}
        toReviewCount={totalToReviewCount}
        todayReviewCount={todayReviewCount}
        onStartClick={onVariantModalOpen}
        onAllPassedClick={onAllPassedClick}
        onStartQuizClick={onStartQuiz}
        onAddPersonalCard={openAddPersonalDialog}
        addPersonalCardDisabled={personalCount >= MAX_PERSONAL_MMK_CARDS}
      />

      <MmkPersonalCardDialog
        open={personalDialogOpen}
        mode={personalDialogMode}
        initialItem={editingPersonalItem}
        personalCardsUsed={personalCount}
        onClose={() => setPersonalDialogOpen(false)}
        onSave={handlePersonalDialogSave}
      />

      <TextField
        size="small"
        fullWidth
        placeholder="Search by question or answer"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
        }}
      />

      <Box sx={mmkFiltersWrapperStyles}>
        <Box sx={mmkFilterSectionStyles}>
          <Typography component="span" sx={mmkFilterSectionLabelStyles}>
            Category
          </Typography>
          {isMobileCategory ? (
            <>
              <Button
                onClick={(e) => setCategoryMenuAnchor(e.currentTarget)}
                startIcon={<ListFilter size={16} />}
                endIcon={<ChevronDown size={16} />}
                sx={{
                  ...((
                    getMmkTabButtonStyles(true) as (t: typeof theme) => object
                  )(theme) as object),
                  textTransform: "none",
                  px: 1.5,
                  width: categorySelectWidth,
                  maxWidth: "100%",
                  justifyContent: "flex-start",
                  "& .MuiButton-startIcon": { mr: 1 },
                  "& .MuiButton-endIcon": { ml: "auto", mr: 0 },
                }}
                aria-haspopup="menu"
                aria-expanded={categoryMenuOpen}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                  }}
                >
                  {activeCategory} ({categoryCounts[activeCategory]})
                </Box>
              </Button>
              <Menu
                anchorEl={categoryMenuAnchor}
                open={categoryMenuOpen}
                onClose={() => setCategoryMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    mt: 0.5,
                    width: categorySelectWidth,
                    maxWidth: "100%",
                    maxHeight: 360,
                    borderRadius: 2,
                  },
                }}
              >
                {sortedCategories.map((cat) => (
                  <MenuItem
                    key={cat}
                    selected={activeCategory === cat}
                    onClick={() => handleFilterClick(cat)}
                    sx={{
                      fontWeight: activeCategory === cat ? 700 : 500,
                      fontSize: "14px",
                    }}
                  >
                    {cat} ({categoryCounts[cat]})
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <Box sx={mmkCategoryTabsStyles}>
              {sortedCategories.map((cat) => (
                <Button
                  key={cat}
                  onClick={() => handleFilterClick(cat)}
                  sx={getMmkTabButtonStyles(activeCategory === cat, cat)}
                >
                  {cat} ({categoryCounts[cat]})
                </Button>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={mmkControlsSectionStyles}>
          <Typography component="span" sx={mmkFilterSectionLabelStyles}>
            View &amp; sort
          </Typography>
          <Box sx={mmkControlsRowStyles}>
            <Button
              onClick={() => handleViewClick("scroll")}
              sx={getMmkTabButtonStyles(viewMode === "scroll")}
              startIcon={<Rows3 size={16} />}
            >
              Deck
            </Button>
            <Button
              onClick={() => handleViewClick("grid")}
              sx={getMmkTabButtonStyles(viewMode === "grid")}
              startIcon={<Monitor size={16} />}
            >
              Grid
            </Button>
            <Button
              onClick={() =>
                onSortOrderChange(
                  sortOrder === "oldest_first"
                    ? "newest_first"
                    : "oldest_first",
                )
              }
              sx={getMmkTabButtonStyles(false)}
              startIcon={
                sortOrder === "oldest_first" ? (
                  <ArrowDownAZ size={16} />
                ) : (
                  <ArrowUpAZ size={16} />
                )
              }
            >
              {sortOrder === "oldest_first" ? "Oldest first" : "Newest first"}
            </Button>
            <Button
              onClick={() => {
                onToggleSelectionMode();
                trackMmkEvent("Toggle selection mode");
              }}
              sx={getMmkTabButtonStyles(isSelectionMode)}
              startIcon={
                isSelectionMode ? (
                  <CheckSquare size={16} />
                ) : (
                  <Square size={16} />
                )
              }
            >
              Select
            </Button>
          </Box>
        </Box>
      </Box>

      {selectedIds.size > 0 && (
        <MmkBatchActions
          selectedCount={selectedIds.size}
          onHide={onBatchHide}
          onPractice={onBatchPractice}
          onClear={onClearSelection}
        />
      )}

      <MmkCardsGrid
        title="Pinned"
        items={pinnedItems}
        sectionKey="pinned"
        viewMode={viewMode}
        activeCardId={activeCardId}
        flippedCardId={flippedCardId}
        isSelectionMode={isSelectionMode}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
        emptyTitle="No pinned cards"
        emptySubtitle="Pin cards to keep priority points at the top."
        onFlip={onCardFlip}
        onCloseFlip={onCloseFlip}
        onPin={onPin}
        onReviewLater={onReviewLater}
        onKnowIt={onKnowIt}
        onEditPersonal={openEditPersonalDialog}
        onDeletePersonal={openDeletePersonalDialog}
      />

      <MmkCardsGrid
        title="To Review"
        items={toReviewItems}
        sectionKey="to_review"
        viewMode={viewMode}
        activeCardId={activeCardId}
        flippedCardId={flippedCardId}
        isSelectionMode={isSelectionMode}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
        emptyTitle="No review cards"
        emptySubtitle="All cards in this category are currently known."
        onFlip={onCardFlip}
        onCloseFlip={onCloseFlip}
        onPin={onPin}
        onReviewLater={onReviewLater}
        onKnowIt={onKnowIt}
        onEditPersonal={openEditPersonalDialog}
        onDeletePersonal={openDeletePersonalDialog}
      />

      <MmkCardsGrid
        title="I Know It"
        items={knownItems}
        sectionKey="known"
        viewMode={viewMode}
        activeCardId={activeCardId}
        flippedCardId={flippedCardId}
        isSelectionMode={isSelectionMode}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
        emptyTitle={allKnown ? "All known - great job!" : "No known cards yet"}
        emptySubtitle={
          allKnown
            ? "You've mastered all cards in this category."
            : "Cards marked as I Know It will appear here."
        }
        onFlip={onCardFlip}
        onCloseFlip={onCloseFlip}
        onPin={onPin}
        onReviewLater={onReviewLater}
        onKnowIt={onKnowIt}
        onEditPersonal={openEditPersonalDialog}
        onDeletePersonal={openDeletePersonalDialog}
      />

      <Dialog
        open={!!deleteDialogId}
        onClose={(_, reason) => {
          if (reason === "backdropClick" && deleteInProgress) return;
          closeDeletePersonalDialog();
        }}
        fullWidth
        maxWidth="xs"
        slotProps={{
          root: {
            sx: { zIndex: 2100 },
          },
          paper: {
            sx: { borderRadius: 2 },
          },
        }}
      >
        <DialogTitle>Delete personal card?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently deletes your personal MMM card. You can&apos;t undo
            this action.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2 }}
            onClick={closeDeletePersonalDialog}
            disabled={deleteInProgress}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none", borderRadius: 2 }}
            onClick={() => void confirmDeletePersonal()}
            disabled={deleteInProgress}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <FocusSessionVariantModal
        open={variantModalOpen}
        onClose={onVariantModalClose}
        selected={focusSessionVariant}
        onSelect={onVariantSelect}
        selectedCategory={focusSessionCategory}
        onCategoryChange={onCategorySelect}
        onStart={onStartFocusSession}
        canStart={canStartFocusSession}
      />
    </MmkPageShell>
  );
};
